
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { DateTime } from 'luxon';
import { omit } from 'lodash';

import { getEnumerationOptions, getOptionLabel } from '../../../../../framework/service/options';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import SwitchField from '../../../../../framework/fields/SwitchField.vue';
import { sharedState } from '../../../../../framework/state';
import { dateToDbDateString } from '../../../../../../common/framework/util/convert';

import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { VehicleContract } from '../../../../../../common/application/model/VehicleContract';
import { SmartFleetContractType } from '../../../../../../common/application/model/enums/SmartFleetContractType';
import { SalesInvoiceRow } from '../../../../../../common/application/model/SalesInvoiceRow';
import { InvoiceRowType } from '../../../../../../common/application/model/enums/InvoiceRowType';
import { Customer } from '../../../../../../common/application/model/Customer';

import AutoCompleteMultipleField from '../../../../fields/AutoCompleteMultipleField.vue';

import { getContractsForRetroactiveInvoicing } from '../../../../client/sales_invoice_client';
import { formatCurrency } from '../../../../service/helper_service';
import { getAccessibleCustomers } from '../../../../service/resource_service';
import { putResource } from '../../../../../framework/client/resource';
import ParameterService from '../../../../service/parameter_service';

@Component({
    components: {
        SelectField,
        AutoCompleteMultipleField,
        SwitchField,
    },
})
export default class RetroactiveSalesInvoices extends Vue {
    readonly resourceType = ApplicationResource.VEHICLE_CONTRACT;
    parameterService = new ParameterService(ApplicationResource.VEHICLE_COST_MONTHLY_REPORT);

    @Prop(String) readonly customerId!: string;
    @Prop(String) readonly mode!: string;

    shared = sharedState;
    local = {
        rows: [] as (VehicleContract & { salesInvoiceRows: SalesInvoiceRow[] })[],
        total: 0,
        loading: false,
        selected: undefined as (VehicleContract & { salesInvoiceRows: SalesInvoiceRow[] }) | undefined,
        smartFleetContractTypeOptions: [] as { id: string | undefined; label: string }[],
        result: '',
        parameters: this.parameterService.get(),
        customers: [] as Customer[],
        showIgnored: false,
    };

    async mounted() {
        this.local.smartFleetContractTypeOptions = getEnumerationOptions(
            'SmartFleetContractType',
            Object.keys(SmartFleetContractType),
            this,
        );

        await this.loadAsyncData();
    }

    dropdownClosed(resourceIds: string[], idField: string): void {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        this.resetPagingAndLoadData();
    }

    @Watch('customerId')
    selectedCustomerIdChanged() {
        if (this.local.loading) {
            return;
        }

        this.resetPagingAndLoadData();
    }

    @Watch('local.showIgnored')
    async showIgnoredChanged(showIgnored: boolean) {
        if (this.local.loading) {
            return;
        }

        await this.loadAsyncData(showIgnored);
    }

    addNextMonthlyInvoice() {
        this.$router.push('/sales-invoice/add-next-monthly');
    }

    confirmMarkAsIgnorable(row: (VehicleContract & { salesInvoiceRows: SalesInvoiceRow[] }) | undefined): void {
        if (!row) {
            return;
        }

        const startOfMonth = DateTime.utc().startOf('month').toFormat('MM/yyyy');
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmIgnoreRetroactiveRows').toString(),
            message: this.$t('message.confirmIgnoreRetroactiveRows', [
                row.vehicleRegistrationNumber,
                startOfMonth,
            ]).toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                await this.markAsIgnorable(omit(row, ['salesInvoiceRows']));
            },
        });
    }

    async markAsIgnorable(vehicleContract: VehicleContract): Promise<void> {
        await putResource<VehicleContract>(ApplicationResource.VEHICLE_CONTRACT, vehicleContract.id, {
            ...vehicleContract,
            ignoreRetroactiveRows: DateTime.utc().startOf('month').toJSDate(),
        });
        await this.loadAsyncData();
    }

    getCustomerName(customerId: string): string {
        const customer = this.local.customers.find(({ id }) => customerId === id);
        return customer ? customer.name : '-';
    }

    getSmartFleetContractType(id: string): string {
        return getOptionLabel(id, this.local.smartFleetContractTypeOptions);
    }

    dateToString(date: string | undefined): string {
        if (!date) {
            return '';
        }

        return dateToDbDateString(new Date(date));
    }

    daysBetweenHandoverAndStart(row: VehicleContract & { salesInvoiceRows: SalesInvoiceRow[] }): number {
        const dailyRent = row.salesInvoiceRows.find(
            (salesRow) => salesRow.invoiceRowType === InvoiceRowType.DAILY_RENT,
        );
        if (!dailyRent) {
            return 0;
        } else {
            return dailyRent.quantity;
        }
    }

    calculateDailyRentPayment(row: VehicleContract & { salesInvoiceRows: SalesInvoiceRow[] }): number {
        const dailyRent = row.salesInvoiceRows.find(
            (salesRow) => salesRow.invoiceRowType === InvoiceRowType.DAILY_RENT,
        );
        if (!dailyRent) {
            return 0;
        } else {
            return dailyRent.unitPrice * dailyRent.quantity;
        }
    }

    calculateMissingMonthlyPayment(row: VehicleContract & { salesInvoiceRows: SalesInvoiceRow[] }): number {
        return row.salesInvoiceRows.reduce((payment: number, salesInvoiceRow: SalesInvoiceRow): number => {
            return payment + salesInvoiceRow.quantity * salesInvoiceRow.unitPrice;
        }, 0);
    }

    onRowClick(row: VehicleContract) {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    getRowClass(row: VehicleContract) {
        if (!row.ignoreRetroactiveRows) {
            return '';
        }

        return 'is-warning';
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }

    private async loadAsyncData(showIgnored = false) {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();
        this.local.customers = await getAccessibleCustomers();

        if (this.customerId) {
            backendParameters.set('customerId', this.customerId);
        }

        this.local.rows = [];

        if (!this.local.parameters.customerId || !this.local.parameters.customerId.length) {
            this.local.loading = false;
            return;
        }

        const rows: (VehicleContract & { salesInvoiceRows: SalesInvoiceRow[] })[] =
            await getContractsForRetroactiveInvoicing(this.local.parameters.customerId);

        rows.forEach((row) => {
            if (showIgnored) {
                this.local.rows.push(row);
            } else {
                if (!row.ignoreRetroactiveRows) {
                    this.local.rows.push(row);
                }
            }
        });

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        this.local.loading = false;
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();

        this.$forceUpdate();
    }
}
